import { GetCompanySearchResponse } from '@iwoca/lapi-client/edge';
import {
  TGetStateResponseData,
  TGetStateResponseDataDE,
  TGetStateResponsePeople,
  TGetStateResponseResidentialAddress,
} from 'lendingApi/lapiOverrides.types';
import _ from 'lodash';
import { removeAllFalselyValues } from 'utils/common/common';
import { convertCurrencyToNumber } from 'utils/currencyConverter';
import { DE_LOCALE } from 'utils/i18n';
import { v4 } from 'uuid';
import { TSignupFormDataDE } from '../NewSignupFormDE/NewSignupFormDE.types';
import {
  TSignupFormData,
  TClientDetailsType,
  TAddressType,
} from '../SignupForm/SignupForm';

// BE -> FE
export function normaliseState(customerState?: TGetStateResponseData) {
  if (!customerState) {
    return {};
  }

  const applicants = customerState.application?.people?.filter((person) =>
    person.roles?.includes('applicant'),
  );

  const guarantors = customerState.application?.people?.filter((person) => {
    return person !== applicants?.[0];
  });

  const getAddresses = (addresses?: TGetStateResponseResidentialAddress[]) => {
    const normaliseAddress = (
      address?: TGetStateResponseResidentialAddress,
    ) => ({
      house_number: address?.house_number || '',
      house_name: address?.house_name || '',
      street_line_1: address?.street_line_1 || '',
      street_line_2: address?.street_line_2 || '',
      town: address?.town || '',
      postcode: address?.postcode || '',
      equifax_token: address?.equifax_token || '',
      residential_status: address?.residential_status || undefined,
    });

    if (!addresses) {
      return [normaliseAddress()];
    }

    return addresses.map((address) => normaliseAddress(address));
  };
  // Refactor normalise to group personal details to applicant role
  const getClientDetails = (people?: TGetStateResponsePeople) => {
    if (!people) return [];

    return people.map((person) => {
      return {
        roles: person.roles,
        title: person.title,
        firstName: person.first_name,
        lastName: person.last_name,
        dobDay: person.date_of_birth?.split('-')[2],
        dobMonth: person.date_of_birth?.split('-')[1],
        dobYear: person.date_of_birth?.split('-')[0],
        phoneNumber: person.phones?.[0]?.number,
        // Need to figure out if primary phone number is always the first, or whether we can
        // check which is "main" and which is secondary or other..
        secondaryPhoneNumber: person.phones?.[1]?.number,
        email: person.emails?.[0]?.email,
        // Need to figure out if primary address is always the first, or whether we can
        // check which is "main" and which is secondary or other..
        residentialAddresses: getAddresses(person.residential_addresses),
        privacyPolicyAgreement: person.privacy_policy?.agreed,
        uid: person.uid,
      };
    });
  };

  return {
    // Company information
    companyType: normaliseCompanyType(customerState.application?.company?.type),
    companyName:
      customerState.application?.company?.registered_company_name || '',
    companyNumber: customerState.application?.company?.company_number || '',
    startDateMonth:
      customerState.application?.company?.trading_from_date?.split('-')[1],
    startDateYear:
      customerState.application?.company?.trading_from_date?.split('-')[0],
    last12MonthsTurnover:
      customerState.application?.company?.last_12_months_turnover?.amount?.toString(),
    tradingName: customerState.application?.company?.trading_name,
    website: customerState.application?.company?.website,
    isVatRegistered: normaliseVATStatus(
      customerState.application?.company?.vat_status?.is_vat_registered,
    ),
    // Personal information
    guarantors: getClientDetails(guarantors),
    applicant: getClientDetails(applicants)?.[0],
  };
}

// BE -> FE
export function normaliseStateDE(customerState?: TGetStateResponseDataDE) {
  if (!customerState) {
    return {} as any;
  }

  const applicants = customerState.application?.people?.filter((person) =>
    person.roles?.includes('applicant'),
  );

  const guarantors = customerState.application?.people?.filter((person) => {
    return person !== applicants?.[0];
  });

  const getAddresses = (addresses?: TGetStateResponseResidentialAddress[]) => {
    const normaliseAddress = (
      address?: TGetStateResponseResidentialAddress,
    ) => ({
      house_number: address?.house_number || '',
      street_line_1: address?.street_line_1 || '',
      street_line_2: address?.street_line_2 || '',
      town: address?.town || '',
      postcode: address?.postcode || '',
    });

    if (!addresses) {
      return [normaliseAddress()];
    }

    return addresses.map((address) => normaliseAddress(address));
  };

  const getClientDetails = (people?: TGetStateResponsePeople) => {
    if (!people) return [];

    return people.map((person) => {
      return {
        roles: person.roles || '',
        title: (person.title as 'herr' | 'frau') || '',
        firstName: person.first_name || '',
        lastName: person.last_name || '',
        dobDay: person.date_of_birth?.split('-')[2],
        dobMonth: person.date_of_birth?.split('-')[1],
        dobYear: person.date_of_birth?.split('-')[0],
        phoneNumber: person.phones?.[0]?.number,
        secondaryPhoneNumber: person.phones?.[1]?.number,
        email: person.emails?.[0]?.email,
        residentialAddresses: getAddresses(person.residential_addresses),
        privacyPolicyAgreement: person.privacy_policy?.agreed,
        uid: person.uid,
      };
    });
  };

  const companyAddress = customerState.application?.company?.registered_address;

  return {
    company: {
      isManualInput: !customerState.application?.company?.creditreform_id,
      crefoId: customerState.application?.company?.creditreform_id || '',
      name: customerState.application?.company?.registered_company_name || '',
      street: companyAddress?.street_line_1,
      buildingNumber: companyAddress?.building_number,
      postcode: companyAddress?.postcode,
      town: companyAddress?.town,
      legalForm: customerState.application?.company?.type,
      tradingFromMonth:
        customerState.application?.company?.trading_from_date?.split('-')[1],
      tradingFromYear:
        customerState.application?.company?.trading_from_date?.split('-')[0],
      last12MonthsTurnover:
        customerState.application?.company?.last_12_months_turnover?.amount?.toLocaleString(
          DE_LOCALE,
        ),
      website: customerState.application?.company?.website,
    },

    // Personal information
    guarantor: getClientDetails(guarantors),
    applicant: getClientDetails(applicants)?.[0],
  };
}
// FE -> BE
export function denormaliseState(
  prevState: TGetStateResponseData,
  formData: TSignupFormData,
): TGetStateResponseData {
  return {
    ...prevState,
    application: {
      company: {
        ...prevState.application?.company,
        registered_company_name: formData.companyName,
        company_number: formData.companyNumber,
        last_12_months_turnover: {
          amount: convertCurrencyToNumber(formData.last12MonthsTurnover),
        },
        trading_from_date: denormaliseDate(
          '01',
          formData.startDateMonth!,
          formData.startDateYear!,
        ),
        type: formData.companyType || undefined,
        trading_name: formData.tradingName,
        website: denormaliseWebsite(formData.website),
        vat_status: { is_vat_registered: formData.isVatRegistered === 'true' },
      },
      people: [formData.applicant, ...formData.guarantor].map((person) => {
        const matchedPersonFromState = prevState.application?.people?.find(
          (statePerson) => statePerson.uid === person.uid,
        );

        return buildPerson(person, matchedPersonFromState);
      }),
    },
  };
}

// FE -> BE
export function denormaliseStateDE(
  prevState: TGetStateResponseDataDE = {},
  formData: Partial<TSignupFormDataDE>,
): TGetStateResponseDataDE {
  const registered_address = {
    building_number: formData.company?.buildingNumber,
    street_line_1: formData.company?.street,
    postcode: formData.company?.postcode,
    town: formData.company?.town,
  };

  return {
    ...prevState,
    application: {
      company: {
        ...prevState.application?.company,
        creditreform_id: formData.company?.crefoId,
        registered_company_name: formData.company?.name,
        last_12_months_turnover: formData.company?.last12MonthsTurnover
          ? {
              amount: convertCurrencyToNumber(
                formData.company?.last12MonthsTurnover,
                true,
              ),
            }
          : undefined,
        trading_from_date:
          formData.company?.tradingFromMonth &&
          formData.company?.tradingFromYear
            ? denormaliseDate(
                '01',
                formData.company?.tradingFromMonth,
                formData.company?.tradingFromYear,
              )
            : undefined,
        website: denormaliseWebsite(formData.company?.website),
        type: formData.company?.legalForm,
        registered_address: _.isEmpty(registered_address)
          ? undefined
          : registered_address,
      },
      people: formData.applicant
        ? [formData.applicant, ...(formData.guarantor || [])].map((person) => {
            const matchedPersonFromState = prevState.application?.people?.find(
              (statePerson) => statePerson.uid === person.uid,
            );

            return removeAllFalselyValues(
              buildPersonDE(person, matchedPersonFromState),
            );
          })
        : undefined,
    },
  };
}

function normaliseVATStatus(vatStatus?: boolean) {
  if (typeof vatStatus === 'boolean') {
    return vatStatus.toString();
  }

  return undefined;
}

function normaliseCompanyType(
  type?: string,
): GetCompanySearchResponse['data'][number]['company_type'] | undefined {
  if (!type) {
    return undefined;
  }

  if (
    type === 'limited_liability_company' ||
    type === 'limited_liability_partnership' ||
    type === 'public_limited_company'
  ) {
    return type;
  }
  return 'other';
}

/**
 * Denormalise the array of applicant and guarantors to match the values to lending-api put state request.
 */
export function buildBasePerson(
  person: TClientDetailsType,
  prevPerson?: TGetStateResponsePeople[number],
  additionalProps: Partial<TGetStateResponsePeople[number]> = {},
): TGetStateResponsePeople[number] {
  return {
    ...prevPerson,
    uid: prevPerson?.uid || v4(),
    first_name: person.firstName,
    last_name: person.lastName,
    roles: person.roles,
    date_of_birth: denormaliseDate(
      person.dobDay,
      person.dobMonth,
      person.dobYear,
    ),
    phones: [
      {
        number: person.phoneNumber,
        type: 'primary',
        uid:
          prevPerson?.phones?.find((item) => item.type === 'primary')?.uid ||
          v4(),
      },
      {
        number: person.secondaryPhoneNumber,
        type: 'secondary',
        uid:
          prevPerson?.phones?.find((item) => item.type === 'secondary')?.uid ||
          v4(),
      },
    ],
    emails: [
      {
        email: person.email || undefined,
        type: 'primary',
        uid: prevPerson?.emails?.[0]?.uid || v4(),
      },
    ],
    residential_addresses: person.residentialAddresses.map(
      (address: TAddressType, index) => ({
        uid: prevPerson?.residential_addresses?.[index]?.uid || v4(),
        flat_name: address.flat_name || undefined,
        house_name: address.house_name || undefined,
        house_number: address.house_number || undefined,
        postcode: address.postcode || undefined,
        equifax_token: address.equifax_token || undefined,
        street_line_1: address.street_line_1 || undefined,
        street_line_2: address.street_line_2 || undefined,
        town: address.town || undefined,
        residential_status: address.residential_status || undefined,
      }),
    ),
    privacy_policy: {
      agreed: person.privacyPolicyAgreement,
      datetime:
        prevPerson?.privacy_policy?.datetime || new Date().toISOString(),
    },
    ...additionalProps,
  };
}

function buildPerson(
  person: TClientDetailsType,
  prevPerson?: TGetStateResponsePeople[number],
): TGetStateResponsePeople[number] {
  return buildBasePerson(person, prevPerson);
}

function buildPersonDE(
  person: TClientDetailsType,
  prevPerson?: TGetStateResponsePeople[number],
): TGetStateResponsePeople[number] {
  return buildBasePerson(person, prevPerson, { title: (person as any).title });
}

export function denormaliseDate(day?: string, month?: string, year?: string) {
  if (!day || !month || !year) return undefined;

  const twoDigitDay = day.padStart(2, '0');
  const twoDigitMonth = month.padStart(2, '0');

  return `${year}-${twoDigitMonth}-${twoDigitDay}`;
}

function denormaliseWebsite(website?: string) {
  if (!website) {
    return undefined;
  }

  const websiteIncludesHttp = website.match(/(http)(?!s)|(https)/g);

  return websiteIncludesHttp ? website : `https://${website}`;
}
